import { NavigationClose } from '@teamsparta/stack-icons';
import { vars } from '@teamsparta/stack-tokens';
import { useAtom, useSetAtom } from 'jotai';
import React, { useEffect } from 'react';

import type { WebinarDto } from '@/api/webinar';
import OutsideClickedWrapper from '@/components/common/OutsideClickedWrapper';
import { webinarToastAtom } from '@/components/webinar/WebinarToast/WebinarToast';
import { useLockBodyScroll, useOnlineUser } from '@/hooks';
import { useApplyWebinar } from '@/hooks/react-query/webinar';
import { deepCopy } from '@/lib/utils/businessLogics/utils/_helper';
import { sendLog } from '@/lib/utils/send-log';
import ProgressBar from '@/pages/webinar/[title]/components/ProgressBar';
import type { WebinarOrientation } from '@/store/webinar';
import { InitOrientationValue, webinarOrientationAtom } from '@/store/webinar';

import MultiStepForm from '../MultiStepForm';
import { multiFormStepAtom } from '../MultiStepForm/MultiStepForm';
import {
  CurrentStateSurvey,
  DifficultySurvey,
  OrientationQuestion,
  PersonalInfo,
  RecruitSurvey,
  skipNumAtom,
} from './StepForms';
import * as S from './WebinarApplicationModal.style';

type WebinarApplicationModal = {
  webinar: WebinarDto;
  onlineUserId: string;
  setShowApplicationModal: (boolean: boolean) => void;
};
export default function WebinarApplicationModal({
  webinar,
  onlineUserId,
  setShowApplicationModal,
}: WebinarApplicationModal) {
  const setToast = useSetAtom(webinarToastAtom);
  const userInfo = useOnlineUser();
  const { mutate, isSuccess } = useApplyWebinar();
  const [step, setStep] = useAtom(multiFormStepAtom);
  const [skipNum, setSkipNum] = useAtom(skipNumAtom);
  const [orientation, setOrientation] = useAtom(webinarOrientationAtom);

  useLockBodyScroll(true);

  useEffect(() => {
    setOrientation({
      ...deepCopy<WebinarOrientation>(InitOrientationValue),
      name: userInfo?.name || '',
      email: userInfo?.email || '',
      phone: userInfo?.phone || '',
    });
    return () => {
      setStep(0);
      setSkipNum(-1);
      setOrientation(deepCopy<WebinarOrientation>(InitOrientationValue));
    };
  }, [userInfo]);

  const Titles = [
    {
      title: '웨비나 신청하기',
      description: '정확한 안내를 위해 기본 정보를 확인해 주세요.',
    },
    {
      title: '사전 정보',
      description: `${orientation.name}님께 더 유익한 웨비나 구성을 위해 참고할게요.`,
    },
    {
      title: '사전 정보',
      description: `${orientation.name}님에 대해 조금만 더 자세히 알려주세요.`,
    },
    {
      title: '사전 정보',
      description: `${orientation.name}님에 대해 조금만 더 자세히 알려주세요.`,
    },
    {
      title: '사전 질문',
      description: '마지막 문항이에요!',
    },
  ].filter((_, idx) => idx !== skipNum);

  const MultiFormElements = [
    <PersonalInfo key='multi-form-step1' />,
    <CurrentStateSurvey key='multi-form-step2' />,
    <DifficultySurvey key='multi-form-step3' />,
    <RecruitSurvey key='multi-form-step4' />,
    <OrientationQuestion key='multi-form-step5' />,
  ].filter((_, idx) => idx !== skipNum);

  const disabledCondition = [
    orientation.name === '' ||
      orientation.phone === '' ||
      orientation.email === '',
    orientation.survey[0].answer === '',
    orientation.survey[1].answer === '',
    orientation.survey[2].answer === '',
    false,
  ].filter((_, idx) => idx !== skipNum);

  const onSubmit = () => {
    mutate({
      webinarId: webinar._id,
      onlineUserId: onlineUserId,
      name: orientation.name,
      phone: orientation.phone,
      email: orientation.email,
      survey: orientation.survey,
      question: orientation.question,
    });
  };
  useEffect(() => {
    if (isSuccess) {
      sendLog('hh_webinarDetailPage_apply_completed', {
        Experience: orientation.survey[0].answer,
        Intention: orientation.survey[1].answer,
        Time: orientation.survey[2].answer,
        'Pre-Question': !!orientation.question,
      });
      setToast('신청이 완료되었습니다');
      setShowApplicationModal(false);
    }
  }, [isSuccess]);

  return (
    <S.InfoModalBackground>
      <OutsideClickedWrapper setState={setShowApplicationModal}>
        <S.InfoModalWrapper>
          <TitleComponent
            title={Titles[step].title}
            description={Titles[step].description}
            setShowApplicationModal={setShowApplicationModal}
          />
          <ProgressBar
            initProgressRate={30}
            maxProgressRate={95}
            progress={step}
            maxProgress={MultiFormElements.length - 1}
          />
          <S.MultiFromWrapper>
            <MultiStepForm
              elements={MultiFormElements}
              diabledNextBtn={disabledCondition}
              maxStep={MultiFormElements.length}
              lastStepBtnString='웨비나 신청하기'
              onSubmit={onSubmit}
            />
          </S.MultiFromWrapper>
        </S.InfoModalWrapper>
      </OutsideClickedWrapper>
    </S.InfoModalBackground>
  );
}

const TitleComponent = ({ title, description, setShowApplicationModal }) => {
  return (
    <S.ModalHeader>
      <S.TitleWrapper>
        <S.ModalTitle>{title}</S.ModalTitle>
        <S.InfoCloseImg onClick={() => setShowApplicationModal(false)}>
          <NavigationClose size={24} color={vars.neutral[50]} />
        </S.InfoCloseImg>
      </S.TitleWrapper>
      <S.Description>{description}</S.Description>
    </S.ModalHeader>
  );
};
